import React from 'react';
import { useQuery } from 'react-query';
import { getTopFiveRecentlyUploadedProjects } from './project/project.api';
import { RecentlyUploadedProjectsDTO } from './project/project.type';
import { Card, CardContent, Icon, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../common/constants/urls';
import { getFullName, joinDateAndUser } from '../common/utils/common.util';
import DescriptionIcon from '@mui/icons-material/Description';
import { Typography, Box } from 'components/index';
import { Link } from 'react-router-dom';
import keys from 'common/react-query/data-keys';
import { UserDTO } from './user-management/auth.type';
import { GridFilterItem } from '@mui/x-data-grid';
import { CustomTooltip } from './common/CustomTooltip';
interface TopFiveProjectsProps {
  user: UserDTO | null;
}

const getUserProjectsFilterModelItems: (user: UserDTO) => GridFilterItem[] = user => {
  return [{ id: 'createdBy', field: 'createdBy', value: getFullName(user), operator: 'contains' }];
};

const TopFiveProjects: React.FC<TopFiveProjectsProps> = ({ user }) => {
  const {
    data: projects,
    isLoading,
    error,
  } = useQuery<RecentlyUploadedProjectsDTO[]>(user ? keys.userRecentProjects : keys.recentProjects, () =>
    getTopFiveRecentlyUploadedProjects(user?.id ?? null),
  );
  const navigate = useNavigate();

  if (isLoading) return <Skeleton variant='rounded' width={'100%'} height={200} />;
  if (error) return <Typography variant='body1'>Error loading projects.</Typography>;

  const handleListItemClick = (projectId: string) => {
    const url = `${ROUTES.PROJECTS}/${projectId}`;
    window.open(url, '_blank');
  };

  return (
    <Box sx={{ width: '100%', height: '100%', marginTop: 0 }}>
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', paddingBottom: 0, paddingTop: 2, paddingInline: 2 }}>
          <DescriptionIcon color='primary' sx={{ marginRight: 1 }} />
          <Typography variant='h5' color='primary' component='span'>
            {user ? 'My Projects' : 'Recently Uploaded Projects'}
          </Typography>
          <Typography
            component={Link}
            to={ROUTES.PROJECTS + (user ? `?showOnlyMyProjects=true` : '')}
            variant='button'
            fontWeight='regular'
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              flex: 'auto',
              cursor: 'pointer',
            }}
          >
            View All
          </Typography>
        </CardContent>
        <Box p={2}>
          <Box component='ul' display='flex' flexDirection='column' p={0} m={0}>
            {!projects || projects.length === 0 ? (
              <Typography variant='body2' marginLeft='20px'>
                No projects found.
              </Typography>
            ) : (
              projects?.map((project, index) => (
                <Box
                  key={index}
                  component='li'
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  borderRadius='lg'
                  py={1}
                  pr={2}
                  mb={projects.length - 1 === index ? 0 : 1}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box
                    display='flex'
                    flexDirection='column'
                    onClick={() => handleListItemClick(project.id)}
                    sx={{ flex: 1, overflow: 'hidden' }}
                  >
                    <CustomTooltip title={`${project.projectNumber} - ${project.projectTitle}`}>
                      <Typography
                        variant='h6'
                        fontWeight='medium'
                        gutterBottom
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {`${project.projectNumber} - ${project.projectTitle}`}
                      </Typography>
                    </CustomTooltip>
                    <Typography variant='caption' color='dark'>
                      {`${joinDateAndUser(project.createdOn, project.createdBy, 'Uploaded on ')}`}
                    </Typography>
                  </Box>
                  <Box display='flex'>
                    <Typography
                      component={Link}
                      variant='button'
                      to={`${ROUTES.PROJECTS}/${project.id}`}
                      sx={{
                        lineHeight: 0,
                        transition: 'all 0.2s cubic-bezier(.34,1.61,.7,1.3)',
                        p: 0.5,

                        '&:hover, &:focus': {
                          transform: 'translateX(5px)',
                        },
                      }}
                    >
                      <Icon sx={{ fontWeight: 'bold' }}>chevron_right</Icon>
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default TopFiveProjects;
